import { DatePicker } from "@design-system/DatePicker/DatePicker";
import type {
    DataGridListDateEdit,
    DataGridListEditorProps,
} from "../../../columns/listEditors";
import { ListEditTrigger } from "../ListEditTrigger";
import { toCloseAndCommit } from "../listEditActions";
import styles from "./DateListEditor.module.scss";

interface DateListEditorProps<TData> extends DataGridListEditorProps<TData> {
    edit: DataGridListDateEdit<TData>;
}

/**
 * `col.edit.date`: a day `DatePicker` on the cell, bounded by the row. A
 * changed day closes it, then commits the `dateOnly` string; clearing commits
 * `null`.
 */
export const DateListEditor = <TData,>({
    row,
    trigger,
    isOpen,
    onOpenChange,
    onCloseAndCommit,
    edit,
}: Readonly<DateListEditorProps<TData>>) => {
    const currentValue = edit.value(row);
    const pickDay = (day: string | undefined) => {
        // The panel input reports its unchanged text on every blur, also
        // when focus moves to a day; closing then would drop that day's click.
        if ((day ?? null) === currentValue) {
            return;
        }
        if (edit.isClearable !== false) {
            toCloseAndCommit({ edit, row, onCloseAndCommit })(day ?? null);
            return;
        }
        if (day !== undefined) {
            toCloseAndCommit({ edit, row, onCloseAndCommit })(day);
        }
    };
    return (
        <DatePicker
            mode="day"
            variant="panel-input"
            className={styles.root}
            open={isOpen}
            onOpenChange={onOpenChange}
            value={currentValue ?? undefined}
            onValueChange={pickDay}
            clearable={edit.isClearable !== false}
            minDate={edit.minDate?.(row)}
            maxDate={edit.maxDate?.(row)}
            trigger={<ListEditTrigger {...trigger} />}
        />
    );
};
